import { Injectable } from '@angular/core';
import { DrupalRESTService } from './drupal-rest.service';
import { PermissionsService } from './permissions.service';

/**
 * Map menu item relative paths to permission keys.
 * Path-based mapping is more resilient to Drupal menu label changes.
 * Empty string value means always visible.
 */
const MENU_PERMISSION_MAP: Record<string, string> = {
  '/dashboard': '',              // always visible
  '/day-view': 'calendar_view',
  '/week-view': 'calendar_view',
  '/lessons-list': 'calendar_view',
  '/package-list': 'packages_view',
  '/package-list-retired': 'packages_view',
  '/packages-list': 'packages_view',
  '/packages-list-retired': 'packages_view',
  '/enrollment': 'enrollments_view',
  '/payment-reports': 'payments_view',
  '/payment-list': 'payments_view',
  '/students-list': 'students_view',
  '/admin/content/student_accounts/add/student_account': 'students_create',
  '/ami-reports': 'reports_ami',
  '/ami-staff-performance': 'reports_view',
  '/inquiry': 'reports_view',
  '/projections-report': 'reports_view',
  '/canceled-views': 'reports_view',
  '/students-inventory': 'reports_view',
  '/chat-success-rate': 'reports_view',
  '/studio-business-summary': 'reports_view',
  '/royalty-report': 'reports_view',
  '/miscellaneous-report': 'reports_view',
  '/reports/cash-report': 'reports_view',
  '/reports/payments_due': 'reports_view',
  '/reports/payments_past_due': 'reports_view',
  '/reports/payments_made_encompassing': 'reports_view',
  '/reports/payments_made_lesson': 'reports_view',
  '/reports/payments_made_misc': 'reports_view',
  '/reports/payments_made_sundry': 'reports_view',
  '/reports/enrollment-tracking': 'reports_view',
  '/reports/inquiries': 'reports_view',
  '/reports/cancellations': 'reports_view',
  '/reports/service-sales': 'reports_view',
  '/reports/counselors': 'reports_view',
  '/reports/supervisors': 'reports_view',
  '/reports/appointment-sheet': 'reports_view',
  '/reports/us-sales-tax-report': 'reports_view',
  '/reports/us-sales-tax-report-recent': 'reports_view',
  '/reports/sale-no-sale-report': 'reports_view',
  '/reports/staff-success-report': 'reports_view',
  '/reports/staff-tracking-report': 'reports_view',
  '/reports/value-lesson-taught-report': 'reports_view',
  '/reports/weekly-enrollment-sales-tracking-report': 'reports_view',
  '/reports/weekly-student-activity': 'reports_view',
  '/reports/weekly-schedule-totals': 'reports_view',
  '/reports/payroll-report-simple': 'reports_view',
  '/reports/liability-report': 'reports_view',
};

@Injectable({
  providedIn: 'root'
})
export class MenuRESTService {

  constructor(
    private _drupalRESTService: DrupalRESTService,
    private _permissionsService: PermissionsService,
  ) {
    // Re-filter the menu whenever studio permissions reload.
    this._permissionsService.studioPermissionsLoaded.subscribe(() => {
      if (this._rawMenuItems.length > 0) {
        this.loadedMenuItems = this.filterMenu(this._rawMenuItems);
      }
    });
  }

  loadedMenuItems: any = [];

  /** Unfiltered menu from Drupal, kept for re-filtering on studio change. */
  private _rawMenuItems: any[] = [];

  getMenu() {
    let endpoint = '/api_rest/v1/getMainMenu'
    return this._drupalRESTService.httpGET(endpoint)
      .subscribe(data => {
        this._rawMenuItems = data as any[] || [];
        this.loadedMenuItems = this.filterMenu(this._rawMenuItems);
      });
  }

  /**
   * Filter menu items based on the user's permissions.
   * If permissions are not being enforced (no studio role assignments),
   * the full menu is returned unchanged.
   */
  private filterMenu(items: any[]): any[] {
    if (!this._permissionsService.shouldEnforcePermissions()) {
      return items;
    }

    return items
      .map(item => this.filterItem(item))
      .filter(item => item !== null);
  }

  private filterItem(item: any): any | null {
    const path = item.relative || '';
    const permission = MENU_PERMISSION_MAP[path];

    // If this item has nested children, filter those first.
    if (item.below && Array.isArray(item.below)) {
      const filteredChildren = item.below
        .map((child: any) => this.filterItem(child))
        .filter((child: any) => child !== null);

      // If parent has a mapped permission and user lacks it,
      // hide the entire group.
      if (permission && !this._permissionsService.canAccess(permission)) {
        return null;
      }

      // If all children were filtered out, hide the parent too.
      if (filteredChildren.length === 0) {
        return null;
      }

      return { ...item, below: filteredChildren };
    }

    // Leaf item: empty string means always visible.
    if (permission === '') {
      return item;
    }

    // Item not in the map: hide it (allowlist approach).
    if (permission === undefined) {
      return null;
    }

    // Check permission.
    if (!this._permissionsService.canAccess(permission)) {
      return null;
    }

    return item;
  }
}
